import React, { useState, useEffect, useRef } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { startScrapingWithCountries } from "../services/document";
import { patentOffices } from "../constant/patentOffices";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

interface PatentOfficeSelectorProps {
  documentId: string;
}

export default function PatentOfficeSelector({
  documentId,
}: PatentOfficeSelectorProps) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>(["US"]);
  const [searchValue, setSearchValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanged, setHasChanged] = useState(false);
  const [initialSelection, setInitialSelection] = useState<string[]>(["US"]);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close dropdown when clicking outside and call API if there are changes
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        // If dropdown is open and is about to close and changes have been made
        if (open && hasChanged) {
          callScrapingApi(selected);
        }

        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, selected, hasChanged]);

  // API integration
  const callScrapingApi = async (countries: string[]) => {
    if (countries.length === 0) return;

    setIsLoading(true);
    setError(null);

    try {
      await startScrapingWithCountries(documentId, countries.join(","));
      // Reset hasChanged flag after successful API call
      setHasChanged(false);
      // Update initialSelection to match current selection
      setInitialSelection([...countries]);
      setIsLoading(false);
      window.location.reload();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An error occurred while calling the API"
      );
      setIsLoading(false);
      console.error("API call failed:", err);
    }
  };

  const handleSelect = (value: string) => {
    setSelected((current) => {
      const newValue = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];

      // Check if selection is different from initial state
      const isChanged = !areArraysEqual(newValue, initialSelection);
      setHasChanged(isChanged);

      return newValue;
    });
  };

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();

    // Set hasChanged to true only if there were items in the initial selection
    const isChanged = initialSelection.length > 0;
    setHasChanged(isChanged);

    setSelected([]);
  };

  // Helper function to compare arrays
  const areArraysEqual = (arr1: string[], arr2: string[]): boolean => {
    if (arr1.length !== arr2.length) return false;

    const sortedArr1 = [...arr1].sort();
    const sortedArr2 = [...arr2].sort();

    return sortedArr1.every((item, index) => item === sortedArr2[index]);
  };

  const handleOpenToggle = () => {
    if (open) {
      // If closing via the button and changes were made
      if (hasChanged) {
        callScrapingApi(selected);
      }
    } else {
      // Reset hasChanged when opening
      setHasChanged(false);
      // Store current selection as initial when opening
      setInitialSelection([...selected]);
    }

    setOpen(!open);
  };

  const filteredOffices = patentOffices.filter((office) =>
    office.toLowerCase().includes(searchValue.toLowerCase())
  );

  const displayText = () => {
    if (selected.length === 0) {
      return <span>Select offices</span>;
    }

    if (selected.length === 1) {
      return <span>{selected[0]}</span>;
    }

    return (
      <span>
        {selected[selected.length - 1]} +{selected.length - 1}
      </span>
    );
  };

  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id="patent-office-tooltip">Country specific scrapping</Tooltip>
      }
    >
      <div className="patent-office-container">
        <button
          ref={buttonRef}
          className={`patent-office-button ${isLoading ? "loading" : ""}`}
          onClick={handleOpenToggle}
          aria-expanded={open}
          aria-haspopup="listbox"
          role="combobox"
          disabled={isLoading}
        >
          {isLoading ? <span>Loading...</span> : displayText()}
          <ChevronsUpDown className="chevron-icon" />
        </button>

        {error && <div className="api-error-message">{error}</div>}

        {open && (
          <div className="patent-office-dropdown" ref={dropdownRef}>
            <div className="search-container">
              <div className="search-input-container">
                <Search className="search-icon" />
                <input
                  className="search-input"
                  placeholder="Search patent offices..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
              </div>
              <button className="clear-button" onClick={handleClearAll}>
                Clear all
              </button>
            </div>

            <div className="command-list">
              {filteredOffices.length === 0 ? (
                <div className="empty-message">No patent office found.</div>
              ) : (
                <div role="listbox">
                  {filteredOffices.map((office) => (
                    <div
                      key={office}
                      className={`office-item ${
                        selected.includes(office) ? "selected" : ""
                      }`}
                      onClick={() => handleSelect(office)}
                      role="option"
                      aria-selected={selected.includes(office)}
                    >
                      <div
                        className={`checkbox ${
                          selected.includes(office) ? "selected" : ""
                        }`}
                      >
                        {selected.includes(office) && (
                          <Check className="check-icon" />
                        )}
                      </div>
                      <span>{office}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </OverlayTrigger>
  );
}
